import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Tag } from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import { Collapse, StateSwitch } from '../../motion';
import { spacing } from '../../style-utils/theme';
import { useHandoffInbox } from './handoff-context';
import { listHandoffs } from '../../api/handoff-client';
import {
  Handoff,
  HandoffListRole,
} from '../../interfaces/handoff-interfaces';
import { clockDayTime } from '../../function-utils/shift-time';
import { IqHandoffCard } from './iq-handoff-card';
import { GhostButton, SkeletonRow } from './iq-shared.styled';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';
import {
  InboxWrap,
  InboxHead,
  InboxTitle,
  InboxNote,
  TabRow,
  Tab,
  HistoryList,
  HistoryRow,
  RowMain,
  RowWhen,
} from './iq-handoff.styled';

/**
 * THE HANDOVER INBOX — D-25 points 4 and 5, and the mark-read of point 5's
 * companion requirement.
 *
 * ═════════════════════ WHAT "ALREADY HAS IT" MEANS HERE ═════════════════════
 * The newest handover addressed to this reader is rendered EXPANDED, first, with
 * no button in between. They did not ask a question; they opened Lobbero IQ and
 * the previous shift was already there. Everything else on this surface — the
 * tabs, the past list — is below it and closed.
 *
 * ═════════════════════════ WHEN IT IS MARKED READ ═══════════════════════════
 * When the CARD IS ACTUALLY ON SCREEN, not when the badge is fetched. Marking it
 * read from the provider's background poll would mean a handover counts as seen
 * because the app loaded, which is precisely the fact a supervisor is trying to
 * establish when they ask whether the incoming person saw it.
 *
 * The write is fired once per id, tracked in a ref rather than in state: the
 * effect re-runs on every re-render of a card that is already open, and a second
 * POST would be a wasted round trip (the server is idempotent — the FIRST open is
 * the timestamp that stands — so it would not corrupt anything, it would just be
 * noise).
 *
 * ══════════════════════════ THE `sent` TAB IS NOT SYMMETRY ══════════════════
 * D-25 makes "sent vs read" a supervisor's question. The outgoing person is the
 * one who needs to know whether the person they handed to has opened it, and
 * this is where they find out — the card states it in words either way.
 */

/* The past-handovers disclosure unfolds (Collapse) rather than popping in; its
   contents keep the inbox's own column gap inside the animated box. */
const PastBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const roleOf = (
  tab: HandoffListRole,
  h: Handoff,
  meId: string
): 'recipient' | 'outgoing' | 'observer' => {
  if (h.recipient_user_id === meId) return 'recipient';
  if (h.outgoing_user_id === meId) return 'outgoing';

  return tab === 'sent' ? 'outgoing' : 'observer';
};

const rowLabel = (tab: HandoffListRole, h: Handoff): string => {
  if (tab === 'sent') {
    // PENDING IS ITS OWN ANSWER, not a missing name. Since backend migration 076
    // a handover is published to the desk with no recipient and is claimed by
    // whoever clocks in next, so `recipient_user_name` is legitimately empty for
    // minutes or hours. Falling through to "a colleague who has since left"
    // would tell the outgoing person their handover went to a deleted account.
    if (h.pending) return 'Waiting for the next shift';

    return `To ${h.recipient_user_name || 'a colleague who has since left'}`;
  }

  return `From ${h.outgoing_user_name || 'a colleague who has since left'}`;
};

interface Props {
  /** The reader. Used to decide which end of the record they are standing at —
   *  never to filter, which the server already did. */
  currentUserId: string;
  /** Resolve a building_id to its name, or undefined when the caller cannot.
   *
   *  A RESOLVER RATHER THAN ONE NAME, because a handover is addressed to a
   *  PERSON and not to the building this panel happens to have selected. A
   *  manager who covers two desks legitimately receives handovers from both, and
   *  stamping the current selection's name onto all of them would tell the
   *  reader the previous shift happened somewhere it did not. Returning
   *  undefined is a correct answer: the card then names no building at all,
   *  which is honest, rather than naming the wrong one. */
  resolveBuildingName?: (buildingId: number) => string | undefined;
  /** The building this panel is pointed at. Required for the "At this desk"
   *  tab, which asks the server for a BUILDING's handovers rather than the
   *  reader's own — GET /shifts/handoff?role=building 400s without it, so the
   *  tab is disabled rather than offered when nothing is selected. */
  buildingId?: number | null;
}

export const IqHandoffInbox: React.FC<Props> = ({
  currentUserId,
  resolveBuildingName,
  buildingId,
}) => {
  // WHO MAY READ A DESK'S HANDOVERS, rather than only their own. The route is
  // already gated on shift:manage server-side and would answer 403, so this is
  // about not offering a tab that cannot work — a concierge holds shift:clock
  // but not shift:manage, and those are deliberately different gates.
  const { permissions } = useAuth();
  const canReadDesk = can(permissions, PERMISSIONS.SHIFT_MANAGE);
  /* SCOPED READS, because this panel ALWAYS names a desk in its own heading.
     `latest` is the provider's un-scoped answer and rendering it here is how the
     same handover appeared under two different buildings: switch the chip from
     Koktower to Mill House and, until the re-read lands (and permanently, if the
     provider was never told the building), the card is still Koktower's with
     Mill House's name above it. `latestFor` returns a row only when the answer
     in hand is about THIS desk. */
  const { latestFor, unreadHereFor, unreadCount, loading, supported, markRead } =
    useHandoffInbox();
  // NaN, not null, is what an unselected building parses to here (see the
  // buildingId prop's source in iq-chat) — so every guard must be
  // Number.isInteger, never `!= null`. A `building_id=NaN` request 400s.
  const deskId = Number.isInteger(buildingId as number)
    ? (buildingId as number)
    : null;
  const latest = latestFor(deskId);
  // Per-desk when we are looking at a desk; the org-wide figure is reserved for
  // the floating launcher's badge, which is the one surface with no building.
  const unreadHere = unreadHereFor(deskId) ?? unreadCount;

  const [showPast, setShowPast] = useState(false);
  const [tab, setTab] = useState<HandoffListRole>('received');
  const [rows, setRows] = useState<Handoff[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const markedRef = useRef<Set<string>>(new Set());
  // Ids for the two disclosures' aria-controls. useId: this panel can mount in
  // the floating chat and on the handover page at once.
  const uid = useId();
  const pastId = `${uid}-past`;
  const openCardId = `${uid}-open`;

  // ONE PLACE marks read, so the rule cannot drift between the pinned card and
  // the history: the reader must be the RECIPIENT and it must be unread.
  const markOnce = useCallback(
    (h: Handoff | null): void => {
      if (!h || h.read_at) return;
      // `can_mark_read` is the SERVER'S answer to the same question this line
      // used to compute locally, and it also covers the case the local compare
      // could not: a PENDING handover has no recipient at all, so the id compare
      // happened to be false for the right reason by accident. Both are checked
      // — the wire field is authoritative, the id compare keeps the intent
      // legible next to it.
      if (!h.can_mark_read) return;
      if (h.recipient_user_id !== currentUserId) return;
      if (markedRef.current.has(h.id)) return;
      markedRef.current.add(h.id);
      markRead(h.id).then(updated => {
        // Patched from the SERVER'S row, never from a locally invented
        // timestamp. If the write did not happen the list keeps saying "Unread",
        // which is the truth.
        if (!updated) return;
        setRows(prev =>
          prev.map(r => (r.id === updated.id ? updated : r))
        );
      });
    },
    [currentUserId, markRead]
  );

  // The pinned card is on screen from the moment this component mounts, so it
  // is read the moment this component mounts.
  useEffect(() => {
    markOnce(latest);
  }, [latest, markOnce]);

  const loadList = useCallback(async (role: HandoffListRole): Promise<void> => {
    setListLoading(true);
    setListError(null);
    try {
      // building_id travels with role='building' and only with it. The single
      // call site used to send neither, which is why adding this tab needed a
      // change here: the request would have 400'd on a missing building_id.
      // building_id travels on EVERY tab now, not only role='building'. It is
      // mandatory there and optional on the personal tabs, where it stops a
      // floating concierge's "received" list showing the same rows under
      // whichever desk they happen to have selected.
      const res = await listHandoffs({
        role,
        limit: 20,
        ...(Number.isInteger(buildingId as number)
          ? { buildingId: buildingId as number }
          : {}),
      });
      setRows(res.handoffs || []);
    } catch (e) {
      setRows([]);
      setListError(
        e instanceof Error ? e.message : 'Could not read your past handovers.'
      );
    } finally {
      setListLoading(false);
    }
    // buildingId IS a dependency. Without it the callback closes over the id
    // that was selected when the panel mounted, so switching desks would
    // re-request the previous building's handovers and label them with the new
    // building's name.
  }, [buildingId]);

  useEffect(() => {
    if (!showPast) return;
    loadList(tab);
  }, [showPast, tab, loadList]);

  if (!supported) return null;

  const openRow = rows.find(r => r.id === openId) || null;

  return (
    <InboxWrap aria-label="Shift handovers">
      {loading && !latest ? (
        <>
          <SkeletonRow style={{ width: '60%' }} />
          <div style={{ height: 8 }} />
          <SkeletonRow style={{ width: '85%' }} />
        </>
      ) : latest ? (
        <>
          <IqHandoffCard
            handoff={latest}
            viewer={roleOf('received', latest, currentUserId)}
            buildingName={resolveBuildingName?.(latest.building_id)}
          />
          {/* D-25's "two people handing to the same person": showing only the
              newest would silently drop the other, so the second one is
              announced rather than hidden behind a collapsed list. */}
          {unreadHere > 1 ? (
            <InboxNote>
              {`${unreadHere - 1} more unread handover${
                unreadHere - 1 === 1 ? '' : 's'
              } are waiting — open the list below.`}
            </InboxNote>
          ) : null}
        </>
      ) : (
        <InboxNote>
          {/* THE OLD COPY DESCRIBED THE PRE-076 MODEL and is what made an empty
              panel read as a delivery filter: "when someone ends their shift
              and names you" implied you only receive what is addressed to you.
              Delivery has not worked that way since 076 —
              shift.handoff_published goes to STAFF_OF_BUILDING with no
              permission requirement, resolved from access_scope. Empty here
              means no handover has been written yet, not that one was written
              and withheld. */}
          Nothing has been handed over yet. When a shift ends at a building you
          cover, its handover appears here.
        </InboxNote>
      )}

      <InboxHead>
        <InboxTitle>Past handovers</InboxTitle>
        <GhostButton
          type="button"
          aria-expanded={showPast}
          aria-controls={pastId}
          onClick={() => setShowPast(v => !v)}
        >
          {showPast ? 'Hide' : 'Show'}
        </GhostButton>
      </InboxHead>

      <Collapse open={showPast} id={pastId}>
        <PastBody>
          <TabRow role="tablist" aria-label="Handover direction">
            <Tab
              type="button"
              role="tab"
              aria-selected={tab === 'received'}
              $active={tab === 'received'}
              onClick={() => {
                setTab('received');
                setOpenId(null);
              }}
            >
              Handed to me
            </Tab>
            <Tab
              type="button"
              role="tab"
              aria-selected={tab === 'sent'}
              $active={tab === 'sent'}
              onClick={() => {
                setTab('sent');
                setOpenId(null);
              }}
            >
              I handed over
            </Tab>
            {/* AT THIS DESK — everything handed over at this building, whoever
                wrote it and whoever it named. This is what ruling R-2 asks for:
                a manager must see the concierge reports on their buildings, and
                until this tab existed the only two views were "handed to me"
                and "I handed over", both of which are about the READER. A
                manager who was never named on a handover therefore saw an empty
                panel and read it as a delivery failure.

                Only for a reader holding shift:manage — a concierge would get a
                403 — and disabled rather than hidden when no building is
                selected, because role='building' without a building_id is a
                400. Disabled says "pick a desk"; hidden would say "this does
                not exist for you". */}
            {canReadDesk ? (
              <Tab
                type="button"
                role="tab"
                aria-selected={tab === 'building'}
                $active={tab === 'building'}
                disabled={buildingId == null}
                title={
                  buildingId == null
                    ? 'Choose a building to see its handovers'
                    : undefined
                }
                onClick={() => {
                  setTab('building');
                  setOpenId(null);
                }}
              >
                At this desk
              </Tab>
            ) : null}
          </TabRow>

          {/* Skeleton, error, empty, list: four fills of one box, crossfaded. */}
          <StateSwitch
            state={
              listLoading
                ? 'loading'
                : listError
                ? 'error'
                : rows.length === 0
                ? 'empty'
                : 'ready'
            }
          >
            {listLoading ? (
              <SkeletonRow style={{ width: '100%' }} />
            ) : listError ? (
              <InboxNote>{listError}</InboxNote>
            ) : rows.length === 0 ? (
              <InboxNote>
                {tab === 'sent'
                  ? 'You have not handed a shift over yet.'
                  : 'Nothing here yet.'}
              </InboxNote>
            ) : (
              <HistoryList>
                {rows.map(h => (
                  <HistoryRow
                    key={h.id}
                    type="button"
                    $active={openId === h.id}
                    $unread={!h.read_at && h.recipient_user_id === currentUserId}
                    aria-expanded={openId === h.id}
                    aria-controls={openCardId}
                    onClick={() => {
                      const next = openId === h.id ? null : h.id;
                      setOpenId(next);
                      // OPENED, so read. Same rule, same helper.
                      if (next) markOnce(h);
                    }}
                  >
                    <RowMain>{rowLabel(tab, h)}</RowMain>
                    {!h.read_at && h.recipient_user_id === currentUserId ? (
                      <Tag tone="warning">Unread</Tag>
                    ) : null}
                    {h.window?.clamped ? (
                      <Tag tone="warning">Not clocked out</Tag>
                    ) : null}
                    {h.status === 'floor' ? (
                      <Tag tone="neutral">No AI summary</Tag>
                    ) : null}
                    <RowWhen>
                      {clockDayTime(h.created_at, h.window?.tz)}
                    </RowWhen>
                  </HistoryRow>
                ))}
              </HistoryList>
            )}
          </StateSwitch>

          {/* The opened record unfolds under the list rather than appearing
              at full height; closing it folds the space back. */}
          <Collapse open={!!openRow} id={openCardId}>
            {openRow ? (
              <IqHandoffCard
                handoff={openRow}
                viewer={roleOf(tab, openRow, currentUserId)}
                buildingName={resolveBuildingName?.(openRow.building_id)}
              />
            ) : null}
          </Collapse>
        </PastBody>
      </Collapse>
    </InboxWrap>
  );
};
